import {
    PropType,
    defineComponent,
    onMounted,
    ref,
    toRaw,
    watch
} from "vue"

import {
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    Filler,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Tooltip,
    type ActiveElement,
    type ChartEvent,
    type ScriptableContext,
} from "chart.js";
import colorLib     from "@kurkle/color";

Chart.register(
    BarController,
    BarElement,
    CategoryScale,
    Filler,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Tooltip,
);

export const ChartColors = {
    coral   : "rgb(255, 92, 136)",
    amber   : "rgb(255, 197, 92)",
    mint    : "rgb(107, 229, 190)",
    cyan    : "rgb(77, 215, 225)",
    blue    : "rgb(98, 166, 255)",
    violet  : "rgb(168, 108, 255)",
    pink    : "rgb(255, 107, 194)",
    slate   : "rgb(148, 163, 184)",
};

function transparentize(value: string, opacity?: number) {
    const alpha = (opacity === undefined)
        ? (0.5)
        : (1 - opacity);

    return colorLib(value)
        .alpha(alpha)
        .rgbString();
}

type ChartOptionsType = {
    labels: string[];
    datasets: {
        label: string;
        data: number[];
        color: string;
    }[];
    visiblePoints: number;
    graphType?: "line" | "bar";
    stacked?: boolean;
};

export type ChartActivePoint = {
    datasetIndex    : number;
    index           : number;
};

type ChartDatasetInput = ChartOptionsType["datasets"][number];

function createAreaGradient(context: ScriptableContext<"line" | "bar">, color: string) {
    const chart = context.chart;
    const chartArea = chart.chartArea;

    if (!chartArea)
        return transparentize(color, 0.9);

    const gradient = chart.ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);

    gradient.addColorStop(0, transparentize(color, 0.62));
    gradient.addColorStop(0.48, transparentize(color, 0.88));
    gradient.addColorStop(1, transparentize(color, 1));

    return gradient;
}

function createChartDataset(dataset: ChartDatasetInput, graphType: ChartOptionsType["graphType"], stacked = false) {
    const type = graphType || "line";

    return {
        type,
        label           : dataset.label,
        borderColor     : dataset.color,
        backgroundColor : type === "bar"
            ? transparentize(dataset.color, stacked ? 0.28 : 0.78)
            : (context: ScriptableContext<"line" | "bar">) => createAreaGradient(context, dataset.color),
        data            : toRaw(dataset.data),
        borderWidth     : stacked ? 1.5 : 3.5,
        borderRadius    : stacked ? 4 : 8,
        borderSkipped   : false,
        cubicInterpolationMode: "monotone" as const,
        fill            : type === "line",
        pointRadius     : 0,
        pointHoverRadius: 5.5,
        pointHitRadius  : 12,
        pointStyle      : "circle",
        pointBackgroundColor: dataset.color,
        pointBorderColor: "#0b0b10",
        pointBorderWidth: 0,
        pointHoverBorderWidth: 3,
        tension         : 0.48,
        yAxisID         : "y",
    };
}

export default defineComponent({

    props: {
        options: {
            type: Object as () => ChartOptionsType,
            required: true,
        },

        onActivePoint: {
            type: Function as PropType<(point: ChartActivePoint | null) => void>,
            default: undefined,
        },
    },

    setup(props) {
        const refCanvas = ref<HTMLCanvasElement | undefined>();
        const refChart = ref<Chart | undefined>();

        onMounted(() => {
            const datasets      = toRaw(props.options.datasets);
            const labels        = toRaw(props.options.labels);
            const visiblePoints = toRaw(props.options.visiblePoints);

            refChart.value = new Chart(refCanvas.value!, {
                data: {
                    labels,
                    datasets: datasets.map((dataset) => createChartDataset(dataset, props.options.graphType, props.options.stacked)),
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        intersect: false,
                        mode: 'index',
                    },
                    onHover(event: ChartEvent, elements: ActiveElement[]) {
                        const element = elements[0];

                        props.onActivePoint?.(element ? {
                            datasetIndex    : element.datasetIndex,
                            index           : element.index,
                        } : null);
                    },
                    layout: {
                        padding: 0,
                        autoPadding: false,
                    },
                    plugins: {
                        title: {
                            display: false,
                        },
                        legend: {
                            display: false,
                        },
                        tooltip: {
                            backgroundColor: "rgba(9, 9, 13, .96)",
                            borderColor: "rgba(255, 255, 255, .12)",
                            borderWidth: 1,
                            boxHeight: 14,
                            boxPadding: 6,
                            boxWidth: 14,
                            bodyColor: "#f5f5f7",
                            displayColors: true,
                            padding: 12,
                            titleColor: "#a8a8b2",
                            usePointStyle: true,
                        },
                    },
                    scales: {
                        x: {
                            stacked: props.options.stacked,
                            border: {
                                display: false,
                            },
                            title: {
                                display: false,
                            },
                            ticks: {
                                display: false,
                                padding: 0,
                            },
                            grid: {
                                display: false,
                                drawTicks: false,
                            },
                            min: Math.max(0, labels.length - visiblePoints),
                            max: labels.length - 1,
                        },
                        y: {
                            stacked: props.options.stacked,
                            border: {
                                display: false,
                            },
                            title: {
                                display: false,
                            },
                            ticks: {
                                display: false,
                            },
                            grid: {
                                display: false,
                                drawTicks: false,
                            },
                        },
                    },
                    animations: {
                        tension: {
                            duration: 700,
                            easing: "easeOutQuart",
                            from: 0.3,
                            to: 0.48,
                        },
                    },
                    elements: {
                        line: {
                            borderCapStyle: "round",
                            borderJoinStyle: "round",
                        },
                    },
                },
            });

            refChart.value.render();
        });

        watch([
            () => props.options.visiblePoints,
            () => props.options.datasets,
            () => props.options.labels,
            () => props.options.graphType,
            () => props.options.stacked,
        ], ([ visiblePoints, datasets, labels ]) => {
            const chart = toRaw(refChart.value);

            if (chart) {
                chart.data.labels = labels;
                chart.data.datasets = datasets.map((dataset) => createChartDataset(dataset, props.options.graphType, props.options.stacked));

                chart.options.scales!["x"]!.min = Math.max(0, labels.length - visiblePoints);
                chart.options.scales!["x"]!.max = labels.length - 1;
                (chart.options.scales!["x"] as { stacked?: boolean }).stacked = props.options.stacked;
                (chart.options.scales!["y"] as { stacked?: boolean }).stacked = props.options.stacked;
                chart.update("active");
            }
        });

        return () => (
            <div class="premium-chart-canvas">
                <canvas style="width: 100%; height: 100%" ref={refCanvas} />
            </div>
        );
    }
});
